import React from "react";
import { CheckCircle2, AlertTriangle, Trash2, Info } from "lucide-react";

/**
 * Reusable branded modal for alerts and confirmations.
 *
 * Props:
 *  - show (bool)          – whether the modal is visible
 *  - onClose (fn)         – called when Cancel / backdrop clicked
 *  - onConfirm (fn|null)  – if provided, renders a two-button confirm dialog
 *  - title (string)
 *  - message (string)
 *  - type ("success" | "error" | "warning" | "info" | "delete")
 *  - confirmText (string) – label for confirm button (default "OK")
 *  - cancelText (string)  – label for cancel button (default "Cancel")
 *  - loading (bool)       – disables confirm button
 */
const ICON_MAP = {
	success: { Icon: CheckCircle2, color: "#4caf50" },
	error: { Icon: AlertTriangle, color: "#d15a36" },
	warning: { Icon: AlertTriangle, color: "#d15a36" },
	info: { Icon: Info, color: "#d15a36" },
	delete: { Icon: Trash2, color: "#d15a36" },
};

const CustomModal = ({
	show,
	onClose,
	onConfirm,
	title = "",
	message = "",
	type = "info",
	confirmText,
	cancelText = "Cancel",
	loading = false,
}) => {
	if (!show) return null;

	const { Icon, color } = ICON_MAP[type] || ICON_MAP.info;
	const isConfirm = typeof onConfirm === "function";
	const btnLabel = confirmText || (isConfirm ? "Confirm" : "OK");
	const btnColor = type === "delete" ? "#c0392b" : "#d15a36";

	return (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: "rgba(0,0,0,0.5)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				zIndex: 9999,
				padding: "1rem",
			}}
			onClick={onClose}
		>
			<div
				style={{
					backgroundColor: "#2a2a2a",
					borderRadius: "12px",
					padding: "1.5rem",
					maxWidth: "400px",
					width: "100%",
					boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
					border: "1px solid #444",
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div className="text-center mb-3">
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: "50%",
							backgroundColor: `${color}22`,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							marginBottom: "0.75rem",
						}}
					>
						<Icon size={22} color={color} />
					</div>
					{title && (
						<h6 style={{ marginBottom: "0.5rem", color: "#fff" }}>{title}</h6>
					)}
					<p
						style={{
							fontSize: "0.9rem",
							color: "rgba(255,255,255,0.6)",
							marginBottom: 0,
							whiteSpace: "pre-line",
						}}
					>
						{message}
					</p>
				</div>
				<div className="d-flex gap-2">
					{isConfirm && (
						<button
							className="btn flex-grow-1"
							style={{
								backgroundColor: "#444",
								color: "#fff",
								border: "none",
							}}
							onClick={onClose}
						>
							{cancelText}
						</button>
					)}
					<button
						className="btn flex-grow-1"
						style={{
							backgroundColor: btnColor,
							color: "#fff",
							border: "none",
						}}
						onClick={() => {
							if (isConfirm) {
								onConfirm();
							} else {
								onClose();
							}
						}}
						disabled={loading}
					>
						{btnLabel}
					</button>
				</div>
			</div>
		</div>
	);
};

export default CustomModal;
